import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React__default, { useState, useEffect } from "react";
import classNames from "classnames";
import { FormOnColor, FormSize, AnalyticsId, IconSize } from "./constants.js";
import { usePseudoClasses } from "./hooks/usePseudoClasses.js";
import { useUuid } from "./hooks/useUuid.js";
import { getColor } from "./theme/currys/color.js";
import { getAriaDescribedBy } from "./utils/accessibility.js";
import { isMutableRefObject, mergeRefs } from "./utils/refs.js";
import { u as uuid } from "./uuid.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { I as Icon } from "./Icon.js";
import Check from "./components/Icons/Check.js";
import { g as getLabelText, r as renderLabelAsParent } from "./Label.js";
import checkboxStyles from "./components/Checkbox/styles.module.scss";
const Checkbox = React__default.forwardRef((props, ref) => {
  const {
    className,
    checked = false,
    disabled,
    label,
    inputId = uuid(),
    onColor = FormOnColor.onwhite,
    name = inputId,
    size,
    errorText,
    error = !!errorText,
    errorTextId,
    errorWrapperClassName,
    value = getLabelText(label),
    testId,
    required,
    onChange,
    ...rest
  } = props;
  const [isChecked, setIsChecked] = useState(checked);
  const errorTextUuid = useUuid(errorTextId);
  const onWhite = onColor === FormOnColor.onwhite;
  const onGrey = onColor === FormOnColor.ongrey;
  const onBlueberry = onColor === FormOnColor.onblueberry;
  const onInvalid = error || onColor === FormOnColor.oninvalid;
  const onDark = onColor === FormOnColor.ondark;
  const large = size === FormSize.large;
  const { refObject, isFocused } = usePseudoClasses(isMutableRefObject(ref) ? ref : null);
  const mergedRefs = mergeRefs([ref, refObject]);
  const checkboxWrapperClasses = classNames({
    [checkboxStyles["checkbox-wrapper--large"]]: large
  });
  const checkboxLabelClasses = classNames(checkboxStyles["checkbox-label"], {
    [checkboxStyles["checkbox-label--disabled"]]: disabled,
    [checkboxStyles["checkbox-label--on-dark"]]: onDark,
    [checkboxStyles["checkbox-label--large"]]: large,
    [checkboxStyles["checkbox-label__large--checked"]]: large && isChecked,
    [checkboxStyles["checkbox-label__large--focus"]]: large && isFocused,
    [checkboxStyles["checkbox-label__large--on-white"]]: large && onWhite,
    [checkboxStyles["checkbox-label__large--on-grey"]]: large && onGrey,
    [checkboxStyles["checkbox-label__large--on-blueberry"]]: large && onBlueberry,
    [checkboxStyles["checkbox-label__large--on-invalid"]]: large && onInvalid,
    [checkboxStyles["checkbox-label__large--disabled"]]: large && disabled
  });
  const checkboxClasses = classNames(checkboxStyles.checkbox, className);
  const checkboxIconWrapperClasses = classNames(checkboxStyles["checkbox__icon-wrapper"], {
    [checkboxStyles["checkbox__icon-wrapper--on-white"]]: onWhite,
    [checkboxStyles["checkbox__icon-wrapper--on-grey"]]: onGrey,
    [checkboxStyles["checkbox__icon-wrapper--on-invalid"]]: onInvalid,
    [checkboxStyles["checkbox__icon-wrapper--disabled"]]: disabled,
    [checkboxStyles["checkbox__icon-wrapper__regular--checked"]]: !large && isChecked,
    [checkboxStyles["checkbox__icon-wrapper__regular--invalid"]]: !large && isChecked && onInvalid,
    [checkboxStyles["checkbox__icon-wrapper__regular--on-dark"]]: !large && isChecked && onDark,
    [checkboxStyles["checkbox__icon-wrapper__large--checked"]]: large && isChecked,
    [checkboxStyles["checkbox__icon-wrapper__large--invalid"]]: large && onInvalid,
    [checkboxStyles["checkbox__icon-wrapper--on-dark"]]: onDark,
    [checkboxStyles["checkbox__icon-wrapper--on-blueberry"]]: onBlueberry,
    [checkboxStyles["checkbox__icon-wrapper--invalid"]]: onInvalid,
    [checkboxStyles["checkbox__icon-wrapper__large--invalid"]]: large && isChecked && onInvalid,
    [checkboxStyles["checkbox__icon-wrapper__large--disabled"]]: disabled && large && isChecked,
    [checkboxStyles["checkbox__icon-wrapper__large--checked--invalid"]]: large && isChecked && onInvalid,
    [checkboxStyles["checkbox__icon-wrapper__large--checked--disabled"]]: disabled && large && isChecked
  });
  const labelTextClasses = classNames(checkboxStyles["checkbox-label__text"], {
    [checkboxStyles["checkbox-label__text__large--checked"]]: large && isChecked,
    [checkboxStyles["checkbox-label__text__large--invalid"]]: large && isChecked && onInvalid,
    [checkboxStyles["checkbox-label__text--on-dark"]]: onDark,
    [checkboxStyles["checkbox-label__text--disabled"]]: disabled
  });
  let iconColor = getColor("white");
  if (onDark || large && isChecked) iconColor = getColor("blueberry", 900);
  if (onInvalid && large && isChecked) iconColor = getColor("white");
  if (disabled) iconColor = getColor("neutral", 700);
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);
  const onChangeHandler = (e) => {
    if (onChange) {
      onChange(e);
    }
    setIsChecked(!isChecked);
  };
  const getLabelContent = () => {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          id: inputId,
          name,
          className: checkboxClasses,
          type: "checkbox",
          checked: isChecked,
          disabled,
          value,
          ref: mergedRefs,
          "aria-describedby": getAriaDescribedBy(props, errorTextUuid),
          "aria-invalid": error,
          required,
          onChange: onChangeHandler,
          ...rest
        }
      ),
      /* @__PURE__ */ jsx("span", { className: checkboxIconWrapperClasses, children: isChecked && /* @__PURE__ */ jsx(Icon, { color: iconColor, className: checkboxStyles["checkbox__icon"], svgIcon: Check, size: IconSize.XSmall }) })
    ] });
  };
  return /* @__PURE__ */ jsx(ErrorWrapper, { className: errorWrapperClassName, errorText, errorTextId: errorTextUuid, children: /* @__PURE__ */ jsx("div", { "data-testid": testId, "data-analyticsid": AnalyticsId.Checkbox, className: checkboxWrapperClasses, children: renderLabelAsParent(
    label,
    getLabelContent(),
    inputId,
    onColor,
    checkboxLabelClasses,
    labelTextClasses,
    checkboxStyles["checkbox-sublabel-wrapper"],
    large
  ) }) });
});
Checkbox.displayName = "Checkbox";
export {
  Checkbox as C
};
//# sourceMappingURL=Checkbox.js.map
